/**
 * Widget size steps — the fixed sizes a dashboard widget can take (Beta-1 plan P3-10/P3-15).
 *
 * Copied from phone home screens: Small 1×1, Medium 2×1, Wide 4×1, Large 2×2, Extra large 4×2
 * on a 4-column grid (Wide added in P3-20 for KPI rows such as the four REPS quadrant cards —
 * Android has 4×1 widgets too). Narrower screens downscale (P3-15):
 *   - 2 columns (tablet): L and XL both render 2×2, W renders 2×1; S and M keep their shape.
 *   - 1 column (phone): every widget is full-width at its own height (S/M/W → 1 row, L/XL → 2).
 *
 * Pure module — no React, no Firebase — so it can be unit-tested with `node --test`.
 */

export const WIDGET_SIZES = Object.freeze({
  S: Object.freeze({ key: 'S', label: 'Small', w: 1, h: 1 }),
  M: Object.freeze({ key: 'M', label: 'Medium', w: 2, h: 1 }),
  W: Object.freeze({ key: 'W', label: 'Wide', w: 4, h: 1 }),
  L: Object.freeze({ key: 'L', label: 'Large', w: 2, h: 2 }),
  XL: Object.freeze({ key: 'XL', label: 'Extra large', w: 4, h: 2 }),
});

/** Size keys from smallest to largest. */
export const SIZE_ORDER = Object.freeze(['S', 'M', 'W', 'L', 'XL']);

/** Canonical (desktop) column count. Layouts are stored in this column space. */
export const GRID_COLUMNS = 4;

/** Column counts the grid can render. */
export const COLUMN_STEPS = Object.freeze([4, 2, 1]);

/**
 * Container-width thresholds (px) → column count. Measured on the grid container, not the
 * viewport (the sidebar takes ~240 px). Override per grid via the `breakpoints` prop.
 */
export const DEFAULT_BREAKPOINTS = Object.freeze({ tablet: 560, desktop: 880 });

export function isSizeKey(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(WIDGET_SIZES, key);
}

/** `{ w, h }` of a size key in the canonical 4-column space. Throws on unknown keys. */
export function sizeDims(key) {
  if (!isSizeKey(key)) throw new Error(`Unknown widget size "${key}"`);
  const { w, h } = WIDGET_SIZES[key];
  return { w, h };
}

/** Size key for canonical dims, or `null` when the dims are not a size step. */
export function dimsToSize(w, h) {
  return SIZE_ORDER.find((k) => WIDGET_SIZES[k].w === w && WIDGET_SIZES[k].h === h) || null;
}

/** Column count for a measured container width. */
export function columnsForWidth(width, breakpoints = DEFAULT_BREAKPOINTS) {
  const bp = { ...DEFAULT_BREAKPOINTS, ...(breakpoints || {}) };
  if (!Number.isFinite(width) || width <= 0) return GRID_COLUMNS; // unknown width: assume desktop
  if (width < bp.tablet) return 1;
  if (width < bp.desktop) return 2;
  return GRID_COLUMNS;
}

/**
 * Dims a size renders at on a grid with `cols` columns (the downscale rule).
 *   4 cols → canonical dims; 2 cols → L/XL become 2×2; 1 col → width 1, own height.
 */
export function dimsForColumns(key, cols) {
  const base = sizeDims(key);
  if (cols >= GRID_COLUMNS) return base;
  // A Wide widget lays its content out 4-across (the REPS quadrant tiles); once the grid is
  // narrower that content stacks, so Wide needs the second row (responsive pass 2026-09-21).
  if (cols === 2) return { w: Math.min(base.w, 2), h: key === 'W' ? 2 : base.h };
  // 1 column: full width, own height
  return { w: 1, h: key === 'W' ? 2 : base.h };
}

/**
 * Sort + de-duplicate a list of size keys into canonical order, dropping unknown keys.
 * Returns a new array.
 */
export function normalizeSizes(sizes) {
  const set = new Set((sizes || []).filter(isSizeKey));
  return SIZE_ORDER.filter((k) => set.has(k));
}

/**
 * The allowed size closest to arbitrary dims — used when a resize gesture stops between steps.
 * Distance = |Δw| + |Δh| in canonical units; ties go to the smaller size (never grow past
 * what the user dragged to unless nothing smaller exists).
 * Returns the size key, or `null` if `allowed` has no valid keys.
 */
export function nearestAllowedSize({ w, h }, allowed, cols = GRID_COLUMNS) {
  const keys = normalizeSizes(allowed);
  if (keys.length === 0) return null;
  let best = null;
  let bestDist = Infinity;
  for (const k of keys) {
    const d = dimsForColumns(k, cols);
    const dist = Math.abs(d.w - w) + Math.abs(d.h - h);
    if (dist < bestDist) {
      best = k;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Next allowed size up or down from `current` (direction +1 / −1); `current` itself when
 * already at the end of the allowed range. Used by the resize handle stepping and by the
 * keyboard +/− shortcuts (P3-16).
 */
export function stepSize(current, allowed, direction) {
  const keys = normalizeSizes(allowed);
  if (keys.length === 0) return current;
  const idx = keys.indexOf(current);
  if (idx === -1) return direction > 0 ? keys[0] : keys[keys.length - 1];
  const next = Math.min(keys.length - 1, Math.max(0, idx + (direction > 0 ? 1 : -1)));
  return keys[next];
}

/**
 * Default row height for a measured column width, so a Small widget is roughly square on a
 * phone/tablet but rows stop growing on very wide screens.
 */
export function defaultRowHeight(columnWidth) {
  if (!Number.isFinite(columnWidth) || columnWidth <= 0) return 180;
  return Math.round(Math.min(Math.max(columnWidth * 0.75, 140), 220));
}

/**
 * The size a widget's CONTENT should render for, given the grid's column count. Narrow grids
 * change the aspect of a cell, so a downscaled widget picks the closest canonical shape:
 *   2 columns → XL renders as L (2×2); 1 column → S/M are a wide single row (M), L/XL a tall
 *   double row (L). Widgets read this `size` prop to decide how much to show.
 */
export function effectiveSize(key, cols) {
  if (!isSizeKey(key)) throw new Error(`Unknown widget size "${key}"`);
  if (cols >= GRID_COLUMNS) return key;
  if (cols === 2) return key === 'XL' ? 'L' : key === 'W' ? 'M' : key;
  return key === 'S' || key === 'M' || key === 'W' ? 'M' : 'L';
}
